/**
 * Blog locale configuration — shared by routes, sitemap, comments,
 * the translator, and SEO metadata. Client-safe (no server-only imports).
 */

export type BlogLocaleCode = "en" | "es" | "zh" | "ur" | "ar";

export type BlogLocale = {
  code: BlogLocaleCode;
  /** Human-readable label for language switcher pills */
  label: string;
  /** Slug suffix appended to the English base slug, e.g. "-es" (empty for English) */
  slugSuffix: string;
  dir: "ltr" | "rtl";
  /** Value for <html lang=...> and <main lang=...> */
  htmlLang: string;
  /** Value for hreflang="..." */
  hrefLang: string;
  /** Value for og:locale */
  ogLocale: string;
  /** BCP-47 locale for date formatting */
  dateLocale: string;
  /** Index route path, e.g. "/blog", "/es/blog" */
  indexRoute: string;
  readingMode: "words" | "chars";
  readingRate: number;
  ui: BlogUiStrings;
};

type BlogUiStrings = {
  homeLabel: string;
  region: string;
  blogTitle: string;
  blogIntro: string;
  readMore: string;
  latest: string;
  allArticles: string;
  backToBlog: string;
  minRead: (n: number) => string;
  relatedArticles: string;
  moreArticles: string;
  needQuote: string;
  freeEstimates: string;
  getFreeQuote: string;
  callLabel: string;
  readySpotless: string;
  noPressure: string;
  newArticlesSoon: string;
  loadError: string;
  noArticles: string;
  /** "Read in {label}" / "Leer en {label}" etc. */
  switchLanguageLabel: string;
};

export const SUPPORTED_LOCALES: BlogLocale[] = [
  {
    code: "en",
    label: "English",
    slugSuffix: "",
    dir: "ltr",
    htmlLang: "en",
    hrefLang: "en",
    ogLocale: "en_US",
    dateLocale: "en-US",
    indexRoute: "/blog",
    readingMode: "words",
    readingRate: 225,
    ui: {
      homeLabel: "Home",
      region: "Capital Region NY",
      blogTitle: "Exterior Cleaning Blog",
      blogIntro:
        "Explore Capital Region NY exterior cleaning tips, pricing guides, and maintenance answers for homeowners. Read expert advice and plan with confidence.",
      readMore: "Read article \u2192",
      latest: "Latest",
      allArticles: "All articles",
      backToBlog: "Back to all articles",
      minRead: (n) => `${n} min read`,
      relatedArticles: "Related articles",
      moreArticles: "More articles",
      needQuote: "Need a quote in the Capital Region?",
      freeEstimates:
        "Fast, free estimates from a local, licensed & insured team.",
      getFreeQuote: "Get Free Quote",
      callLabel: "Call (518) 900-1913",
      readySpotless: "Ready for a spotless exterior?",
      noPressure:
        "Free, no-pressure quotes across Albany, Schenectady, Clifton Park, Saratoga Springs, Troy and surrounding towns.",
      newArticlesSoon: "New articles are on the way \u2014 check back soon.",
      loadError:
        "We couldn't load the blog right now. Please try again shortly.",
      noArticles: "No articles found.",
      switchLanguageLabel: "Read in",
    },
  },
  {
    code: "es",
    label: "Español",
    slugSuffix: "-es",
    dir: "ltr",
    htmlLang: "es",
    hrefLang: "es",
    ogLocale: "es_US",
    dateLocale: "es-US",
    indexRoute: "/es/blog",
    readingMode: "words",
    readingRate: 225,
    ui: {
      homeLabel: "Inicio",
      region: "Región Capital de Nueva York",
      blogTitle: "Blog de Limpieza Exterior",
      blogIntro:
        "Guías de precios, consejos de mantenimiento y respuestas claras para propietarios de la Región Capital.",
      readMore: "Leer más \u2192",
      latest: "Más reciente",
      allArticles: "Todos los artículos",
      backToBlog: "Volver al blog",
      minRead: (n) => `${n} min de lectura`,
      relatedArticles: "Artículos relacionados",
      moreArticles: "Más artículos",
      needQuote: "¿Listo para un exterior impecable?",
      freeEstimates:
        "Presupuestos gratis y sin compromiso en Albany, Schenectady, Clifton Park, Saratoga Springs, Troy y pueblos cercanos.",
      getFreeQuote: "Presupuesto Gratis",
      callLabel: "Llame al (518) 900-1913",
      readySpotless: "¿Listo para un exterior impecable?",
      noPressure:
        "Presupuestos gratis y sin compromiso en Albany, Schenectady, Clifton Park, Saratoga Springs, Troy y pueblos cercanos.",
      newArticlesSoon: "Pronto publicaremos nuevos artículos \u2014 vuelve pronto.",
      loadError:
        "No pudimos cargar el blog en este momento. Inténtalo de nuevo en unos minutos.",
      noArticles: "No se encontraron artículos.",
      switchLanguageLabel: "Leer en",
    },
  },
  {
    code: "zh",
    label: "中文",
    slugSuffix: "-zh",
    dir: "ltr",
    htmlLang: "zh-Hans",
    hrefLang: "zh-Hans",
    ogLocale: "zh_CN",
    dateLocale: "zh-CN",
    indexRoute: "/zh/blog",
    readingMode: "chars",
    readingRate: 400,
    ui: {
      homeLabel: "首页",
      region: "纽约首都地区",
      blogTitle: "外墙清洁博客",
      blogIntro: "为首都地区房主提供的价格指南、维护技巧和实用建议。",
      readMore: "阅读文章 \u2192",
      latest: "最新",
      allArticles: "所有文章",
      backToBlog: "返回所有文章",
      minRead: (n) => `${n}分钟阅读`,
      relatedArticles: "相关文章",
      moreArticles: "更多文章",
      needQuote: "需要首都地区的报价吗？",
      freeEstimates: "快速、免费的估价，来自本地持牌和投保团队。",
      getFreeQuote: "获取免费报价",
      callLabel: "致电 (518) 900-1913",
      readySpotless: "准备好让外墙焕然一新了吗？",
      noPressure:
        "在 Albany、Schenectady、Clifton Park、Saratoga Springs、Troy 及周边城镇提供免费、无压力的报价。",
      newArticlesSoon: "新文章即将发布 — 请稍后回来查看。",
      loadError: "暂时无法加载博客，请稍后再试。",
      noArticles: "未找到文章。",
      switchLanguageLabel: "阅读",
    },
  },
  {
    code: "ur",
    label: "اردو",
    slugSuffix: "-ur",
    dir: "rtl",
    htmlLang: "ur",
    hrefLang: "ur",
    ogLocale: "ur_PK",
    dateLocale: "ur-PK",
    indexRoute: "/ur/blog",
    readingMode: "words",
    readingRate: 180,
    ui: {
      homeLabel: "صفحہ اول",
      region: "نیویارک کیپیٹل ریجن",
      blogTitle: "بیرونی صفائی بلاگ",
      blogIntro:
        "کیپیٹل ریجن کے گھر مالکان کے لیے قیمت کی رہنمائی، دیکھ بھال کے مشورے اور واضح جوابات۔",
      readMore: "مضمون پڑھیں \u2192",
      latest: "تازہ ترین",
      allArticles: "تمام مضامین",
      backToBlog: "تمام مضامین پر واپس",
      minRead: (n) => `${n} منٹ کا مطالعہ`,
      relatedArticles: "متعلقہ مضامین",
      moreArticles: "مزید مضامین",
      needQuote: "کیپیٹل ریجن میں قیمت درکار ہے؟",
      freeEstimates: "مقامی، لائسنس یافتہ اور انشورڈ ٹیم سے فوری، مفت تخمینہ۔",
      getFreeQuote: "مفت قیمت حاصل کریں",
      callLabel: "کال کریں (518) 900-1913",
      readySpotless: "بیرونی حصے کو چمکدار بنانے کے لیے تیار ہیں؟",
      noPressure:
        "Albany، Schenectady، Clifton Park، Saratoga Springs، Troy اور آس پاس کے علاقوں میں مفت، بغیر دباؤ کے تخمینے۔",
      newArticlesSoon: "نئے مضامین جلد آرہے ہیں — جلد واپس آئیں۔",
      loadError: "ابھی بلاگ لوڈ نہیں ہو سکا۔ براہ کرم تھوڑی دیر بعد کوشش کریں۔",
      noArticles: "کوئی مضامین نہیں ملے۔",
      switchLanguageLabel: "پڑھیں",
    },
  },
  {
    code: "ar",
    label: "العربية",
    slugSuffix: "-ar",
    dir: "rtl",
    htmlLang: "ar",
    hrefLang: "ar",
    ogLocale: "ar_EG",
    dateLocale: "ar-EG",
    indexRoute: "/ar/blog",
    readingMode: "words",
    readingRate: 200,
    ui: {
      homeLabel: "الرئيسية",
      region: "منطقة العاصمة نيويورك",
      blogTitle: "مدونة تنظيف الواجهات الخارجية",
      blogIntro:
        "أدلة الأسعار ونصائح الصيانة وإجابات واضحة لأصحاب المنازل في منطقة العاصمة.",
      readMore: "اقرأ المقالة \u2192",
      latest: "الأحدث",
      allArticles: "جميع المقالات",
      backToBlog: "العودة إلى جميع المقالات",
      minRead: (n) => `${n} دقيقة قراءة`,
      relatedArticles: "مقالات ذات صلة",
      moreArticles: "مزيد من المقالات",
      needQuote: "هل تحتاج عرض سعر في منطقة العاصمة؟",
      freeEstimates: "تقديرات سريعة ومجانية من فريق محلي مرخص ومؤمن.",
      getFreeQuote: "احصل على عرض سعر مجاني",
      callLabel: "اتصل (518) 900-1913",
      readySpotless: "هل أنت مستعد لواجهة خارجية نظيفة؟",
      noPressure:
        "عروض أسعار مجانية وبدون ضغط في Albany و Schenectady و Clifton Park و Saratoga Springs و Troy والمدن المحيطة.",
      newArticlesSoon: "مقالات جديدة قادمة قريباً — تابعنا.",
      loadError: "تعذر تحميل المدونة حالياً. يرجى المحاولة مرة أخرى بعد قليل.",
      noArticles: "لم يتم العثور على مقالات.",
      switchLanguageLabel: "اقرأ",
    },
  },
];

export const BLOG_LOCALE_MAP = Object.fromEntries(
  SUPPORTED_LOCALES.map((l) => [l.code, l]),
) as Record<BlogLocaleCode, BlogLocale>;

/** Non-English locales (the ones that get translated) */
export const TRANSLATED_LOCALES = SUPPORTED_LOCALES.filter(
  (l) => l.code !== "en",
);

export function getLocale(code: string | null | undefined): BlogLocale {
  return BLOG_LOCALE_MAP[code as BlogLocaleCode] ?? BLOG_LOCALE_MAP.en;
}

export function isLocaleCode(code: string): code is BlogLocaleCode {
  return code in BLOG_LOCALE_MAP;
}

/**
 * Normalize any input to a valid locale code.
 * Unknown values default to "en".
 */
export function normalizeLangCode(input: unknown): BlogLocaleCode {
  const raw = String(
    (input as { language?: unknown } | undefined)?.language ?? "en",
  );
  return isLocaleCode(raw) ? raw : "en";
}

/** Strip any known language suffix from a slug to get the English base slug. */
export function stripLangSuffix(slug: string): string {
  for (const loc of SUPPORTED_LOCALES) {
    if (loc.slugSuffix && slug.endsWith(loc.slugSuffix)) {
      return slug.slice(0, -loc.slugSuffix.length);
    }
  }
  return slug;
}

/** Get the locale code from a slug suffix, or "en" if none. */
export function localeFromSlug(slug: string): BlogLocaleCode {
  for (const loc of SUPPORTED_LOCALES) {
    if (loc.slugSuffix && slug.endsWith(loc.slugSuffix)) {
      return loc.code;
    }
  }
  return "en";
}

/**
 * Resolve the document language for a URL path so <html lang> matches the
 * page's hreflang. Handles both localized index routes ("/es/blog") and
 * translated article slugs ("/blog/my-post-es").
 */
export function localeFromPath(pathname: string): BlogLocale {
  const path = pathname.replace(/\/+$/, "") || "/";
  for (const loc of SUPPORTED_LOCALES) {
    if (loc.code === "en") continue;
    if (path === `/${loc.code}` || path.startsWith(`/${loc.code}/`)) return loc;
  }
  if (path.startsWith("/blog/")) {
    return getLocale(localeFromSlug(path.slice("/blog/".length)));
  }
  return getLocale("en");
}

/** Compute the translation slug for a base slug + locale. */
export function translationSlug(baseSlug: string, locale: BlogLocaleCode): string {
  const loc = BLOG_LOCALE_MAP[locale];
  return loc.slugSuffix ? `${baseSlug}${loc.slugSuffix}` : baseSlug;
}

/** Reading-time calculation respecting per-locale mode and rate. */
export function readingMinutes(
  html: string | null | undefined,
  locale: BlogLocaleCode = "en",
): number {
  if (!html) return 0;
  const loc = BLOG_LOCALE_MAP[locale];
  if (loc.readingMode === "chars") {
    // For CJK languages, count non-space, non-tag characters.
    const text = html.replace(/<[^>]+>/g, "").replace(/\s+/g, "");
    return text ? Math.max(1, Math.round(text.length / loc.readingRate)) : 0;
  }
  const words = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean).length;
  return words ? Math.max(1, Math.round(words / loc.readingRate)) : 0;
}

/** Hreflang alternate links for blog index pages. */
export function blogIndexAltLinks(
  site: string,
): Array<{ rel: "alternate"; hrefLang: string; href: string }> {
  return [
    ...SUPPORTED_LOCALES.map((l) => ({
      rel: "alternate" as const,
      hrefLang: l.hrefLang,
      href: `${site}${l.indexRoute}`,
    })),
    {
      rel: "alternate" as const,
      hrefLang: "x-default",
      href: `${site}/blog`,
    },
  ];
}

/**
 * Given an article's slug and language, plus its translated_from_slug,
 * compute the slugs of all potential sibling translations.
 */
export function siblingSlugs(
  slug: string,
  language: string | null,
  translatedFromSlug: string | null,
): Array<{ locale: BlogLocaleCode; slug: string }> {
  const base =
    language && language !== "en"
      ? (translatedFromSlug ?? stripLangSuffix(slug))
      : slug;

  return SUPPORTED_LOCALES.map((l) => ({
    locale: l.code,
    slug: l.slugSuffix ? `${base}${l.slugSuffix}` : base,
  }));
}

/**
 * Google Fonts <link>s for the non-Latin scripts (Arabic, Urdu Nastaliq,
 * Simplified Chinese). Only attached to the routes that actually render those
 * scripts — loading it globally added ~92 KiB of render-blocking CSS to every
 * English page.
 */
const SCRIPT_FONT_FAMILY: Partial<Record<BlogLocaleCode, string>> = {
  ar: "Noto+Naskh+Arabic:wght@400;600;700",
  ur: "Noto+Nastaliq+Urdu:wght@400;600;700",
  zh: "Noto+Sans+SC:wght@400;500;700",
};

export function scriptFontLinks(
  code: string | null | undefined,
): Array<Record<string, string>> {
  const family = SCRIPT_FONT_FAMILY[(code ?? "en") as BlogLocaleCode];
  if (!family) return [];
  return [
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
    {
      // `display=optional` avoids a mid-scroll reflow on long translated
      // articles: if the webfont isn't ready at first paint the system script
      // font is kept for that view instead of swapping in late.
      rel: "stylesheet",
      href: `https://fonts.googleapis.com/css2?family=${family}&display=optional`,
    },
  ];
}
